﻿@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.enginetabular.allFromTabularCommit
import org.example.project.service.InfrastrukturApi
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

object LhmTypCreateDialogOverlay {
    var content by mutableStateOf<(@Composable () -> Unit)?>(null)
        private set

    fun show(dialogContent: @Composable () -> Unit) {
        content = dialogContent
    }

    fun close() {
        content = null
    }
}

@Composable
fun LhmTypCreateDialogOverlayLayer() {
    LhmTypCreateDialogOverlay.content?.invoke()
}

@Composable
fun CUSLhmTypverwaltung(art: String = org.example.project.service.currentSaveArt()) {
    var typen by remember { mutableStateOf<List<All>>(emptyList()) }
    var registerTypen by remember { mutableStateOf<List<All>>(emptyList()) }
    var createDialogOpen by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    LaunchedEffect(art) {
        registerTypen = InfrastrukturApi.loadLhmRegister().sortedBy { it.id.toIntOrNull() ?: 0 }
        typen = InfrastrukturApi.loadLhmTypen().sortedBy { it.id.toIntOrNull() ?: 0 }
    }

    val headers = listOf("ID", "Bezeichnung", "Kurz Bezeichnung")
    val saveFields = listOf("id", "bezeichnung", "kurzbezeichnung")
    fun nextId(): String =
        ((typen.maxOfOrNull { it.id.toIntOrNull() ?: 0 } ?: 0) + 1).toString()

    fun appendLhmTyp(prefill: All): All {
        val existing = typen.firstOrNull {
            it.bezeichnung.trim().equals(prefill.bezeichnung.trim(), ignoreCase = true) &&
                it.bezeichnung.isNotBlank()
        }
        if (existing != null) return existing
        val draft = prefill.copy(id = nextId())
        typen = (typen + draft).sortedBy { it.id.toIntOrNull() ?: 0 }
        return draft
    }

    fun registerEntry(entry: All): All =
        All(id = entry.id, bezeichnung = entry.bezeichnung, kurzbezeichnung = entry.kurzbezeichnung)

    TabularScreen(
        screenKey = "LhmTypVerwaltung",
        rollenKey = "LhmTypVerwaltung",
        title = "LHM-Typverwaltung",
        headers = headers,
        models = typen,
        data = typen.map { t ->
            t.id to listOf(
                t.id,
                t.bezeichnung,
                t.kurzbezeichnung
            )
        },
        editable = true,
        nonEditableCols = setOf("ID"),
        createRow = { _ ->
            listOf(nextId(), "", "")
        },
        onCreateAction = { createDialogOpen = true },
        onCommit = { rows ->
            scope.launch {
                rows.forEach { (id, cols) ->
                    val existing = typen.firstOrNull { it.id == id }
                    val toSave = allFromTabularCommit(
                        id,
                        listOf(
                            id,
                            cols[1],
                            cols[2]
                        ),
                        saveFields
                    )
                    val changed = existing == null || existing != toSave
                    if (changed) {
                        InfrastrukturApi.saveLhmRegister(registerEntry(toSave))
                        InfrastrukturApi.saveLhmTyp(toSave)
                    }
                }
                registerTypen = InfrastrukturApi.loadLhmRegister().sortedBy { it.id.toIntOrNull() ?: 0 }
                typen = InfrastrukturApi.loadLhmTypen().sortedBy { it.id.toIntOrNull() ?: 0 }
            }
        },
        onDeleteRows = { ids -> ids.forEach { id -> InfrastrukturApi.deleteLhmTyp(id) } }
    )

    DisposableEffect(createDialogOpen, registerTypen, typen) {
        if (createDialogOpen) {
            LhmTypCreateDialogOverlay.show {
                LhmTypVorlagenDialog(
                    entries = registerTypen,
                    onCreate = { entry ->
                        scope.launch {
                            val draft = appendLhmTyp(entry)
                            InfrastrukturApi.saveLhmTyp(draft)
                            registerTypen = InfrastrukturApi.loadLhmRegister().sortedBy { it.id.toIntOrNull() ?: 0 }
                            typen = InfrastrukturApi.loadLhmTypen().sortedBy { it.id.toIntOrNull() ?: 0 }
                            createDialogOpen = false
                        }
                    },
                    onCreateBlank = {
                        appendLhmTyp(All())
                        createDialogOpen = false
                    },
                    onClose = { createDialogOpen = false }
                )
            }
        } else {
            LhmTypCreateDialogOverlay.close()
        }
        onDispose {
            LhmTypCreateDialogOverlay.close()
        }
    }
}

@Composable
private fun LhmTypVorlagenDialog(
    entries: List<All>,
    onCreate: (All) -> Unit,
    onCreateBlank: () -> Unit,
    onClose: () -> Unit
) {
    val tabularRows = remember(entries) {
        entries.map { entry ->
            entry.id to listOf(
                entry.bezeichnung,
                entry.kurzbezeichnung
            )
        }
    }
    val entryByRowId = remember(entries) { entries.associateBy { it.id } }
    var selectedValue by remember(tabularRows) { mutableStateOf(tabularRows.firstOrNull()?.first.orEmpty()) }
    val selectedEntry = entryByRowId[selectedValue]

    PopupEngineCard(
        windowKey = "lhm-typen-vorlagen-auswahl",
        widthPx = 620,
        heightPx = 460,
        minWidthPx = 560,
        minHeightPx = 380,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                property("padding", "44px 20px 20px")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                boxSizing("border-box")
            }
        }) {
            Div({
                style {
                    textAlign("center")
                    fontSize(18.px)
                    fontWeight("700")
                    color(DashboardTheme.TEXT_LIGHT)
                }
            }) {
                Text("LHM-Typ aus Register übernehmen")
            }

            Div({
                style {
                    flex(1)
                    minHeight(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                if (entries.isEmpty()) {
                    Div({
                        style {
                            flex(1)
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            alignItems(AlignItems.Center)
                        }
                    }) {
                        Text("Keine LHM-Typen-Vorlagen vorhanden.")
                    }
                } else {
                    TabularScreen(
                        screenKey = "",
                        title = null,
                        headers = listOf("Bezeichnung", "Kurzbezeichnung"),
                        data = tabularRows,
                        models = emptyList(),
                        editable = false,
                        useUserSettings = false,
                        createButtonLabel = "Erstellen",
                        onCreateAction = onCreateBlank,
                        allowSave = false,
                        allowDelete = false,
                        allowImportButton = false,
                        allowRefreshButton = false,
                        allowExportButton = false,
                        allowSettingsButton = false,
                        nonEditableCols = setOf("Bezeichnung", "Kurzbezeichnung"),
                        includeMetadata = false,
                        selectedRowIndices = emptySet(),
                        onRowClick = { rowId -> selectedValue = rowId },
                        extraNavButtons = listOf(
                            TabularToolbarButton(
                                label = "Übernehmen",
                                icon = "images/joinnetwork.svg",
                                enabled = selectedEntry != null,
                                onClick = {
                                    selectedEntry?.let { entry -> onCreate(entry.copy(id = "")) }
                                }
                            )
                        ),
                        smartMode = true,
                        onCommit = { }
                    )
                }
            }
        }
    }
}

val standardLhmTypen: List<All> = listOf(
    All(id = "1", bezeichnung = "Euro Palette", kurzbezeichnung = "EURO"),
    All(id = "2", bezeichnung = "Düsseldorfer Palette", kurzbezeichnung = "DÜS"),
    All(id = "3", bezeichnung = "Gitterbox", kurzbezeichnung = "GIBO"),
    All(id = "4", bezeichnung = "Einwegpalette", kurzbezeichnung = "EINWEG")
)

